import { hashJson } from '@basehub/utils/hash'
import { invariant } from '@epic-web/invariant'
import get from 'lodash/get'
import set from 'lodash/set'
import cloneDeep from 'lodash/cloneDeep'
import { diff } from './lib'

export interface DiffOptions {
  verbose?: boolean
  raw?: boolean
  keysOnly?: boolean
  full?: boolean
  sort?: boolean
  outputKeys?: string[]
  excludeKeys?: string[]
  keepUnchangedValues?: boolean
  outputNewOnly?: boolean
  maxElisions?: number
  precision?: number
  customComparator?: (a: any, b: any) => boolean | undefined
}

export type DiffStatus = '+' | '-' | '~' | `->${number}` | ' '
export type DiffItem = [DiffStatus, any] & { _index: number }

export { diff }

const defaultObjectHash = (obj: any) => hashJson(obj)

export function diffArrays(
  a: any[],
  b: any[],
  _options?: DiffOptions & {
    recursivePath?: string
    reorders?: {
      enabled: boolean
      objectHash?: (obj: DiffItem[1]) => string
    }
  }
): Array<DiffItem> {
  const {
    recursivePath = 'content',
    reorders = {
      enabled: true,
      objectHash: defaultObjectHash,
    },
    full = true,
    ...options
  } = _options ?? {}

  let raw = (diff(a, b, { ...options, full }) || []) as Array<DiffItem>

  if (reorders.enabled) {
    const insertionsToClear = new Set<number>()
    const hashesMap: Map<number, string> = new Map()
    const withReorders = raw.map((item, i) => {
      const status = getStatusFromDiffItem(item)
      if (status !== 'removed') return item
      const hash = reorders.objectHash?.(item[1]) ?? defaultObjectHash(item[1])
      if (!hash) return item
      hashesMap.set(i, hash)
      const insertionMatchIdx = raw.findIndex((item2, i2) => {
        const status2 = getStatusFromDiffItem(item2)
        if (status2 !== 'added') return false
        if (insertionsToClear.has(i2)) return false
        const hash2FromMap = hashesMap.get(i2)
        if (hash2FromMap) {
          return hash2FromMap === hash
        }
        const hash2 =
          reorders.objectHash?.(item2[1]) ?? defaultObjectHash(item2[1])
        if (!hash2) return false
        hashesMap.set(i2, hash2)
        return hash2 === hash
      })
      if (insertionMatchIdx !== -1) {
        insertionsToClear.add(insertionMatchIdx)
        const reorderTargetIdx = raw
          .slice(0, insertionMatchIdx)
          .filter((item3) => getStatusFromDiffItem(item3) !== 'removed').length
        return [`->${reorderTargetIdx}`, item[1]]
      }
      return item
    })

    if (insertionsToClear.size > 0) {
      raw = withReorders.filter(
        (_item, index) => !insertionsToClear.has(index)
      ) as Array<DiffItem>
    } else {
      raw = withReorders as Array<DiffItem>
    }
  }

  function addIndexToDiffItemRecursively(_item: DiffItem, index: number) {
    const itemClone = cloneDeep(_item)
    itemClone._index = index
    const dataInRecursivePath = get(itemClone[1], recursivePath)
    if (Array.isArray(dataInRecursivePath)) {
      set(
        itemClone[1],
        recursivePath,
        dataInRecursivePath.map((nextItem, idx) => {
          return addIndexToDiffItemRecursively(nextItem as DiffItem, idx)
        })
      )
    }
    return itemClone
  }

  return raw.map((item, idx) => {
    return addIndexToDiffItemRecursively(item, idx)
  })
}

export function getStatusFromDiffItem(item: DiffItem | undefined) {
  if (Array.isArray(item)) {
    const status = item[0]
    switch (status) {
      case '+':
        return 'added'
      case '-':
        return 'removed'
      case '~':
        return 'modified'
      default:
        if (status.startsWith('->')) {
          return 'reordered'
        }
        return 'unmodified'
    }
  } else return 'unmodified'
}

export function getReorderTargetFromDiffItem(
  item: DiffItem | undefined
): number | undefined {
  const status = getStatusFromDiffItem(item)
  if (status !== 'reordered') return undefined
  const targetIndex = Number(item?.[0].replace('->', ''))
  invariant(isNaN(targetIndex) === false, 'targetIndex is required')
  return targetIndex
}

/**
 * Creates a custom comparator for ProseMirror link marks
 * Treats objects that only differ by having default values as equal
 *
 * @example
 * // Use like this:
 * diff(oldDoc, newDoc, {
 *   customComparator: richTextComparator
 * })
 */
export function richTextComparator(a: any, b: any): boolean | undefined {
  // Link marks migration
  if (a?.type === 'link' || b?.type === 'link') {
    if (a?.attrs && b?.attrs) {
      if (
        (!a.attrs.type || a.attrs.type === 'link') &&
        (!b.attrs.type || b.attrs.type === 'link')
      ) {
        if (a.attrs.href === b.attrs.href) {
          return true
        }
      }
    }
  }

  // Return undefined to let the standard comparison flow continue
  return undefined
}
